/**
 * apps/web :: pass :: screens/CoursewareLesson
 *
 * Single light-courseware lesson for the active country pack
 * (ROADS-569 W1.C5). Resolves the lesson by `:lessonId` route param
 * within the active pack's course modules and renders the course title
 * eyebrow, the lesson heading + body, its key points as bullets, and —
 * when present — a single-choice knowledge check that reveals whether
 * the chosen option is correct on click. A "Next" link advances to the
 * following lesson, or back to the course when on the last lesson. An
 * unknown / missing lessonId falls back to a back-to-course link.
 *
 * Wrapped in PassLayout for consistent pass chrome.
 */
import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import PassLayout from "../PassLayout";
import {
  getPack,
  useActivePassCountries,
  type PackId,
} from "@/lib/pass-content";

export default function CoursewareLesson(): JSX.Element {
  const country = useActivePassCountries()[0] ?? "IT";
  const pack = getPack(country as PackId);
  const { course } = pack;
  const { lessonId } = useParams<{ lessonId: string }>();

  const index = course.modules.findIndex((l) => l.id === lessonId);
  const lesson = index >= 0 ? course.modules[index] : undefined;

  const [selected, setSelected] = useState<number | null>(null);

  if (!lesson) {
    return (
      <PassLayout country={country} title={course.title}>
        <p className="text-sm text-neutral-700" data-testid="lesson-not-found">
          That lesson could not be found.
        </p>
        <Link
          to="../learn"
          data-testid="lesson-back-to-course"
          className="mt-lg inline-block text-sm font-semibold text-indigo-600 underline hover:text-indigo-800"
        >
          Back to course
        </Link>
      </PassLayout>
    );
  }

  const next = index < course.modules.length - 1 ? course.modules[index + 1] : undefined;

  return (
    <PassLayout country={country} title={lesson.title}>
      <p
        className="text-xs font-semibold uppercase tracking-wide text-indigo-600"
        data-testid="lesson-eyebrow"
      >
        {course.title}
      </p>
      <h1
        className="mt-1 font-display text-xl font-bold tracking-tight text-neutral-900"
        data-testid="lesson-title"
      >
        {lesson.title}
      </h1>

      <p className="mt-md text-sm leading-relaxed text-neutral-700">
        {lesson.body}
      </p>

      <h2 className="mt-lg text-xs font-semibold uppercase tracking-wide text-neutral-500">
        Key points
      </h2>
      <ul className="mt-sm flex flex-col gap-1.5">
        {lesson.keyPoints.map((point, i) => (
          <li
            key={i}
            data-testid={`lesson-keypoint-${i}`}
            className="flex items-start gap-2 text-sm text-neutral-700"
          >
            <span aria-hidden="true" className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-500" />
            <span>{point}</span>
          </li>
        ))}
      </ul>

      {lesson.check ? (
        <section className="mt-lg rounded-md border border-neutral-200 px-md py-md" data-testid="lesson-check">
          <p className="text-sm font-semibold text-neutral-900" data-testid="lesson-check-q">
            {lesson.check.q}
          </p>
          <ul className="mt-sm flex flex-col gap-2">
            {lesson.check.options.map((option, i) => {
              const isSelected = selected === i;
              const state = !isSelected
                ? null
                : option.correct
                  ? "correct"
                  : "incorrect";
              return (
                <li key={i}>
                  <button
                    type="button"
                    data-testid={`lesson-check-option-${i}`}
                    data-state={state ?? undefined}
                    onClick={() => setSelected(i)}
                    className={`w-full rounded-md border px-md py-sm text-left text-sm ${
                      state === "correct"
                        ? "border-green-500 bg-green-50 text-green-800"
                        : state === "incorrect"
                          ? "border-red-500 bg-red-50 text-red-800"
                          : "border-neutral-200 hover:border-indigo-300 hover:bg-indigo-50"
                    }`}
                  >
                    {option.label}
                    {state === "correct" ? (
                      <span className="ml-2 font-semibold" data-testid="lesson-check-correct">
                        Correct
                      </span>
                    ) : null}
                    {state === "incorrect" ? (
                      <span className="ml-2 font-semibold" data-testid="lesson-check-incorrect">
                        Not quite
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      <div className="mt-lg">
        {next ? (
          <Link
            to={`../learn/${next.id}`}
            data-testid="lesson-next"
            className="inline-block text-sm font-semibold text-indigo-600 underline hover:text-indigo-800"
          >
            Next: {next.title}
          </Link>
        ) : (
          <Link
            to="../learn"
            data-testid="lesson-next"
            className="inline-block text-sm font-semibold text-indigo-600 underline hover:text-indigo-800"
          >
            Back to course
          </Link>
        )}
      </div>
    </PassLayout>
  );
}
